import { Button } from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import i18n from 'modules/core/lib/i18n'

import { BenefitsEmployeeActivation } from 'modules/benefits/pages/EmployeesPage/ActivateBenefitsModal'
import { type EmployeeDetailsMap } from 'modules/benefits/pages/EmployeesPage/ActivationResultSidePanel'

interface ActivationResultCalloutProps {
  activationData: BenefitsEmployeeActivation & {
    employeeDetailsMap?: EmployeeDetailsMap
  }
  onReviewClick?: () => void
  compact?: boolean
}

type CalloutType = 'warning' | 'info'

interface CalloutConfig {
  type: CalloutType
  description: string
  showAction: boolean
}

const getEmployeesWithIssuesCount = (activationData: BenefitsEmployeeActivation): number => {
  const { successfulEmployees: _successfulEmployees, ...issueFields } = activationData

  const uniqueEmployeesWithIssues = new Set<number>()
  Object.values(issueFields).forEach((field) => {
    if (Array.isArray(field)) {
      field.forEach((employeeId) => uniqueEmployeesWithIssues.add(employeeId))
    }
  })

  return uniqueEmployeesWithIssues.size
}

const getCalloutConfig = (successfulCount: number, issuesCount: number): CalloutConfig | null => {
  const hasSuccesses = successfulCount > 0
  const hasIssues = issuesCount > 0

  if (!hasSuccesses && !hasIssues) {
    return null
  }

  if (hasSuccesses && hasIssues) {
    return {
      type: 'warning',
      description: i18n.t('benefits.tabs.employees.activation_results.mixed', {
        successfulCount,
        issuesCount,
      }),
      showAction: true,
    }
  }

  if (!hasSuccesses && hasIssues) {
    return {
      type: 'warning',
      description: i18n.t('benefits.tabs.employees.activation_results.only_failures', {
        issuesCount,
      }),
      showAction: true,
    }
  }

  return {
    type: 'info',
    description: i18n.t('benefits.tabs.employees.activation_results.only_successes', {
      successfulCount,
    }),
    showAction: false,
  }
}

export const CalloutResults = ({
  activationData,
  onReviewClick,
  compact = true,
}: ActivationResultCalloutProps) => {
  const successfulCount = activationData.successfulEmployees?.length || 0
  const issuesCount = getEmployeesWithIssuesCount(activationData)

  const config = getCalloutConfig(successfulCount, issuesCount)
  if (!config) return null

  return (
    <Callout
      compact={compact}
      type={config.type}
      description={config.description}
      action={
        config.showAction && onReviewClick ? (
          <Button onClick={onReviewClick} type="secondary">
            {i18n.t('benefits.tabs.employees.activation_results.review_missing_data')}
          </Button>
        ) : undefined
      }
    />
  )
}
